import type {ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';

/**
 * Custom hook `useTextareaField` containing the logic for the `TextareaField` component.
 * It manages the field state and provides a function to handle change events triggered by the textarea input.
 * It also allows an optional `onChange` callback to be invoked when the field's value changes.
 *
 * @param name          The name of the form field.
 * @param initialValue  The initial value of the field.
 * @param keepOnUnmount (Optional) A boolean indicating whether to keep the field value on unmount. Defaults to `false`.
 * @param validation    (Optional) A validation function to validate the field value.
 * @param onChange      (Optional) A callback function triggered when the field value changes.
 *
 * @returns An object containing:
 * - `handleChange`: A function that handles change events on the textarea input and updates the field value.
 * - `value`: The current state of the field.
 *
 * @example
 * ```tsx
 * const { handleChange, value } = useTextareaField('description', 'Initial value');
 * handleChange({ target: { value: 'Updated value' } });
 * console.log(value); // Outputs: "Updated value"
 * ```
 */
export const useTextareaField = (
    name: string,
    initialValue: string,
    keepOnUnmount = false,
    validation?: ValidationFunc,
    onChange?: (value: string) => void
) => {
    const {error, handleFieldChange, value} = useFormField(name, initialValue, initialValue, validation, keepOnUnmount);
    const colors = useThemeColors();

    /**
     * Event handler for handling change events on an input element.
     *
     * @param event The change event.
     */
    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        handleFieldChange(event.target.value);
        onChange?.(event.target.value);
    };

    return {
        colors,
        error,
        handleChange,
        value
    };
};